import React, { useEffect, useState } from "react";

import { dateFormat } from "common/dateFormat";
import { getTabToken } from "common/MetaActions";
import DatePicker from "components/datePicker/DatePicker";
import OverlayLoader from "components/loader/OverlayLoader";
import { captureEvent } from "components/posthog/customEventCapture";
import config from "config/config";
import { Banner } from "models/admin/admin";

import { apiRequest, baseApiRequest } from "api/apiInterceptor";
import "./BannerMessage.scss";
import "./SystemMessage.scss";

export default function BannerMessage() {
  const [pendingBannersList, setPendingBannersList] = useState<Banner[]>([]);
  const [banner, setBanner] = useState<Banner>({
    id: "",
    message: "",
    url: "",
    linkText: "",
    startDate: "",
    endDate: "",
    activeProjects: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hideStartDateCalendar, setHideStartDateCalendar] = useState(true);
  const [hideEndDateCalendar, setHideEndDateCalendar] = useState(true);

  useEffect(() => {
    getAllBanners();
  }, []);

  const getAllBanners = async () => {
    setIsEditing(true);
    try {
      const res = await baseApiRequest
        .get(config.api.banners.getAll())
        .set("Authorization", "Bearer " + getTabToken())
        .send();
      setPendingBannersList(res.body);
    } catch (err) {
      console.error("Failed to fetch banners", err);
    } finally {
      setIsEditing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateMandatoryFields()) {
      return;
    }

    setIsEditing(true);
    try {
      const method = banner.id ? "put" : "post";
      const url = banner.id
        ? config.api.banners.put(banner.id)
        : config.api.banners.post();
      const { id, ...bannerData } = banner;
      const dataToSend = {
        ...bannerData,
        startDate: new Date(bannerData.startDate).toISOString(),
        endDate: new Date(bannerData.endDate).toISOString()
      };

      await apiRequest[method](url).send(dataToSend);

      clearFormFields();
      await getAllBanners();
    } catch (err) {
      console.error("Failed to save banner", err);
    } finally {
      setIsEditing(false);
    }
  };

  const handleEditBanner = (id: string) => {
    const bannerInfo = pendingBannersList.find((item) => item.id === id);
    if (bannerInfo) {
      setBanner({ ...bannerInfo });
    }
  };

  const handleDeleteBanner = async (id: string) => {
    setIsEditing(true);
    try {
      await baseApiRequest
        .delete(config.api.banners.delete(id))
        .set("Authorization", "Bearer " + getTabToken())
        .send();
      await getAllBanners();
      setBanner({ ...banner, id: "" });
    } catch (err) {
      console.error("Failed to delete banner", err);
    } finally {
      setIsEditing(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setBanner((prev) => ({ ...prev, [name]: value }));
  };

  const setStartDate = (date: Date) => {
    setBanner((prev) => ({ ...prev, startDate: date.toISOString() }));
    setHideStartDateCalendar(true);
  };

  const setEndDate = (date: Date) => {
    setBanner((prev) => ({ ...prev, endDate: date.toISOString() }));
    setHideEndDateCalendar(true);
  };

  const clearFormFields = () => {
    setBanner({
      id: "",
      message: "",
      url: "",
      linkText: "",
      startDate: "",
      endDate: "",
      activeProjects: true
    });
    setIsEditing(false);
    setErrorMessage("");
  };

  const validateMandatoryFields = () => {
    setErrorMessage("");
    if (!banner.message) {
      setErrorMessage("Fält för bannermeddelande är obligatoriskt.");
      return false;
    }
    if (!banner.startDate) {
      setErrorMessage("Fält för startdatum är obligatoriskt.");
      return false;
    }
    if (!banner.endDate) {
      setErrorMessage("Fält för slutdatum är obligatoriskt.");
      return false;
    }
    return true;
  };

  const showBannersList = () => {
    if (!pendingBannersList.length) {
      return <p className="noActiveBanners">No active banners</p>;
    }
    return pendingBannersList.map((object) => (
      <div className="bannerMessageList" key={object.id}>
        <div>
          <span>
            {object.message}
            <a href={object.url}>
              <u> {object.linkText || object.url}</u>
            </a>
          </span>
          <button
            type="button"
            className="buttonDelete"
            onClick={() => {
              handleDeleteBanner(object.id);
              captureEvent("Delete");
            }}
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => {
              handleEditBanner(object.id);
              captureEvent("Edit");
            }}
          >
            Edit
          </button>
        </div>
        <div>
          <small>{dateFormat(new Date(object.startDate))}</small>
        </div>
      </div>
    ));
  };

  const isDateDisabled = (date: Date, isStartDate = true) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (isStartDate) {
      return date < today;
    }
    const startDate = new Date(banner.startDate);
    const dateToCompare = startDate > today ? startDate : today;
    return date < dateToCompare;
  };

  return (
    <div>
      <>{showBannersList()}</>
      <div>
        <button
          className="buttonSecondary"
          type="submit"
          onClick={() => {
            clearFormFields();
            captureEvent("Skapa ny banner");
          }}
        >
          Skapa ny banner
        </button>
      </div>

      <br />
      <form onSubmit={handleSubmit} className="bannerMessageForm">
        <OverlayLoader isLoading={isEditing}>
          <div>
            <fieldset disabled={isEditing}>
              <p>Bannermeddelande *</p>
              <textarea
                name="message"
                value={banner.message}
                onChange={(e) => handleChange(e)}
              />
            </fieldset>
            <fieldset className="bannerFieldset" disabled={isEditing}>
              <p>URL</p>
              <input
                name="url"
                type="text"
                placeholder="https://exempel.com"
                value={banner.url}
                onChange={handleChange}
              />
              <br />
              <p>Länktext</p>
              <input
                type="text"
                name="linkText"
                value={banner.linkText}
                onChange={handleChange}
                disabled={banner.url === ""}
              />
              <br />
              <div className="dateInputs">
                <p>Startdatum *</p>
                <input
                  name="startDate"
                  type="text"
                  value={
                    banner.startDate !== ""
                      ? dateFormat(new Date(banner.startDate))
                      : ""
                  }
                  readOnly={true}
                  onClick={() =>
                    setHideStartDateCalendar(!hideStartDateCalendar)
                  }
                />
                <div hidden={hideStartDateCalendar}>
                  <DatePicker
                    onChange={(newValue: Date) => setStartDate(newValue)}
                    disabledDays={(date) => isDateDisabled(new Date(date))}
                    value={new Date(banner.startDate)}
                  />
                </div>
              </div>
              <div className="dateInputs">
                <p>Slutdatum *</p>
                <input
                  name="endDate"
                  type="text"
                  value={
                    banner.endDate !== ""
                      ? dateFormat(new Date(banner.endDate))
                      : ""
                  }
                  readOnly={true}
                  onClick={() => setHideEndDateCalendar(!hideEndDateCalendar)}
                />
                <div hidden={hideEndDateCalendar}>
                  <DatePicker
                    onChange={(newValue: Date) => setEndDate(newValue)}
                    disabledDays={(date) =>
                      isDateDisabled(new Date(date), false)
                    }
                    value={new Date(banner.endDate)}
                  />
                </div>
              </div>
            </fieldset>
            <br />
            <span>
              <input
                type="checkbox"
                name="activeProjects"
                checked={banner.activeProjects}
                onChange={() =>
                  setBanner({
                    ...banner,
                    activeProjects: !banner.activeProjects
                  })
                }
              />
              Filtrera på projekttyperna EAY, EBY and EDY
            </span>
            <br />
            <p style={{ color: "red" }}>{errorMessage}</p>
            <button
              className="buttonSecondary"
              type="submit"
              onClick={() => captureEvent("Spara")}
            >
              Spara
            </button>

            <br />
          </div>
        </OverlayLoader>
      </form>
    </div>
  );
}
